import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Label } from 'reactstrap';
import Select from 'react-select';
import { adminTeamMembersActions } from '../redux/actions';
import api from '../api';
import history from '../history';
import Pagination from '../components/Pagination';
import {
  Table, TableHead, TableRow, TableBody, TableCell,
} from '../components/CustomTable';
import { CardLoader } from '../components/Loader';
import Page from '../components/Page';
import DashboardHead from '../components/DashboardHead';
import BrowserView from '../components/BrowserView';
import MobileUnsupported from '../components/MobileUnsupported';
import TabletUnsupported from '../components/TabletUnsupported';
import useError from '../utils/HookErrorUtil';
import Head from '../components/AdminHead';
import AdminTeamForm from '../components/AdminTeamForm';
import NewTooltip from '../components/NewTooltip';
import TextUtil from '../utils/TextUtil';

const PAGE_SIZE = 20;
const TEXT_MAX_LENGTH = 40;

export function AdminTeamPage(props) {
  const { admin } = props;
  const { id } = useParams();

  const { handleCatched } = useError();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [adminTeam, setAdminTeam] = useState(null);

  const dispatch = useDispatch();

  const fetchAdminTeam = async () => {
    try {
      setLoading(true);
      const team = await api.read('admin-teams', Number(id), {}, { admin: true });
      setAdminTeam(team);
      // Fetch members
      await dispatch(adminTeamMembersActions.list(
        { team: Number(id) },
        { pagination: 'no', admin: true },
      ));
    } catch (error) {
      handleCatched(props, error);
      history.push('/admin/admin-teams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminTeam();
  }, [id]);

  return (
    <Page
      {...props}
      title={t('user:admin-team.title')}
    >
      <DashboardHead
        {...props}
        title={t('user:admin-team.title')}
        breadcrumdPrevious={[]}
      />
      <div className="dashboard-content mb-2">
        <div className="card bg-white table-responsive contains-loader card-shadow p-4 mt-3 mobile-card overflow-x-visible">
          {loading && <CardLoader />}
          {adminTeam && (
            <>
              <div className="mb-3">
                <h4 className="font-weight-normal list-title mb-0">
                  {adminTeam.name}
                </h4>
                <p className="text-muted mb-0">
                  {t('user:admin-team.organization')}
                  :
                  {' '}
                  {adminTeam.owner_display_name}
                </p>
              </div>
              <div className="pt-3">
                <AdminTeamForm
                  admin={admin}
                  adminTeam={adminTeam}
                  onUpdate={fetchAdminTeam}
                  {...props}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Page>
  );
}

AdminTeamPage.propTypes = {
  admin: PropTypes.bool,
};

AdminTeamPage.defaultProps = {
  admin: false,
};

function AdminTeamFilter(props) {
  const {
    items, onChange, placeholder, labelKey, className,
  } = props;

  const itemOptions = items.map((item) => ({ label: item[labelKey], value: item.id }));

  return (
    <Select
      className={`react-select ${className}`}
      classNamePrefix="react-select"
      onChange={(e) => {
        let newValue;
        if (e) {
          newValue = Number(e.value);
        }
        onChange(newValue);
      }}
      options={itemOptions}
      placeholder={placeholder}
      isClearable
    />
  );
}

AdminTeamFilter.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  placeholder: PropTypes.string.isRequired,
  className: PropTypes.string,
  labelKey: PropTypes.string,
  onChange: PropTypes.func,
};

AdminTeamFilter.defaultProps = {
  className: '',
  labelKey: 'label',
  onChange: () => {},
};

function AdminTeams(props) {
  const { t } = useTranslation();
  const { handleCatched } = useError();

  let searchDelay;

  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [adminTeams, setAdminTeams] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  // Filters
  const [owners, setOwners] = useState([]);
  const [filters, setFilters] = useState({ owner: undefined });

  const fetchAdminTeams = async (pageNumber = 1) => {
    try {
      setLoading(true);
      const params = {
        page: pageNumber,
        page_size: PAGE_SIZE,
      };

      if (filters.owner) {
        params.owner = filters.owner;
      }

      const response = await api.list('admin-teams', params, { admin: true });
      setAdminTeams(response.results || []);
      setTotalCount(response.count || 0);
      setPage(pageNumber);
      setPageSize(Math.max(response.results?.length || 0, pageSize));
    } catch (error) {
      handleCatched(props, error);
    } finally {
      setLoading(false);
      setReady(true);
    }
  };

  const fetchOwners = async () => {
    try {
      const res = await api.list(
        'users',
        { admin_team_owner: true },
        { pagination: 'no', admin: true },
      );
      setOwners(res);
    } catch (error) {
      handleCatched(props, error, false);
    }
  };

  const fetchData = async () => {
    await Promise.all([fetchAdminTeams(1), fetchOwners()]);
  };

  const handleSearch = async (key = '', value = '') => {
    const newFilters = { [key]: value };
    await setFilters((currentFilters) => ({ ...currentFilters, ...newFilters }));
  };

  const handleSearchOwner = (value) => handleSearch('owner', value);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    clearTimeout(searchDelay);
    searchDelay = setTimeout(() => fetchAdminTeams(1), 250);
  }, [filters.owner]);

  const handleRowClick = (teamId) => {
    history.push(`/admin/admin-team/${teamId}`);
  };

  return (
    <Page
      {...props}
      title={t('user:admin-team.list-title')}
    >
      <Head
        title={t('user:admin-team.list-title')}
        subTitle={t('user:admin-team.description')}
        admin
        {...props}
      />
      <div className="dashboard-content mb-2">
        <div className="card bg-white table-responsive contains-loader card-shadow p-4 mt-3 mobile-card overflow-x-visible">
          <BrowserView>
            <div className="row justify-content-between align-items-center mt-3">
              <div className="col-12 col-md-auto">
                <div className="row align-items-center">
                  <div className="col-auto text-bigger">
                    {totalCount}
                    &nbsp;
                    {t('user:admin-team.list-title-count', { count: totalCount })}
                  </div>
                  <div className="col-auto d-flex align-items-center">
                    <Label className="mr-2 mb-0">
                      {t('user:admin-team.organization')}
                      {' '}
                      :
                    </Label>
                    <AdminTeamFilter
                      key="owner-select"
                      items={owners}
                      placeholder={t('user:admin-team.filter-organization')}
                      onChange={handleSearchOwner}
                    />
                  </div>
                </div>
              </div>
            </div>
            <Table extraClassName="mt-3 mb-0">
              <TableHead>
                <TableRow>
                  <TableCell>{t('user:admin-team.team-name')}</TableCell>
                  <TableCell>{t('user:admin-team.organization')}</TableCell>
                  <TableCell>{t('user:admin-team.owner')}</TableCell>
                  <TableCell>{t('user:admin-team.type')}</TableCell>
                  <TableCell>{t('user:admin-team.members')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ready && !loading && adminTeams.length > 0 ? (
                  adminTeams.map((team) => {
                    const { name } = team;
                    const truncatedName = TextUtil.truncate(name, TEXT_MAX_LENGTH);
                    return (
                      <TableRow
                        key={team.id}
                        onClick={() => handleRowClick(team.id)}
                      >
                        <TableCell>
                          <NewTooltip
                            content={name}
                            disabled={name === truncatedName}
                          >
                            <span className="font-weight-semibold">
                              {truncatedName}
                            </span>
                          </NewTooltip>
                        </TableCell>
                        <TableCell>{team.owner_display_name}</TableCell>
                        <TableCell>{team.owner_email}</TableCell>
                        <TableCell>
                          {team.owner_type ? t(`user:types.${team.owner_type}`) : ''}
                        </TableCell>
                        <TableCell>{team.members_count || 0}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan="5">
                      {loading || !ready ? (
                        <CardLoader />
                      ) : (
                        <span>{t('user:admin-team.no-admin-teams')}</span>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {totalCount > PAGE_SIZE && (
              <div className="mt-2">
                <Pagination
                  page={page}
                  count={totalCount}
                  pageSize={pageSize}
                  action={fetchAdminTeams}
                />
              </div>
            )}
          </BrowserView>
          <TabletUnsupported />
          <MobileUnsupported>
            common:mobile-and-tablet-unsupported
          </MobileUnsupported>
        </div>
      </div>
    </Page>
  );
}

AdminTeams.propTypes = {
  admin: PropTypes.bool,
};

AdminTeams.defaultProps = {
  admin: false,
};

export default AdminTeams;
